import React, { useState, useCallback } from 'react'
import {
  Eye,
  Edit,
  Plus,
  ArrowRightLeft,
  Upload,
  Download,
  Image as ImageIcon,
  Package,
  Barcode,
  Settings2,
  Trash2,
  RotateCcw,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { SearchFilter } from '../components/ui/SearchFilter'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog'

import { DetailModal } from '../components/inventory/modals/DetailModal'
import { AdjustModal } from '../components/inventory/modals/AdjustModal'
import { EditProductModal } from '../components/inventory/modals/EditProductModal'
import { ImportModal } from '../components/inventory/modals/ImportModal'
import { TransferModal } from '../components/inventory/modals/TransferModal'
import { ReceiveConfirmModal } from '../components/inventory/modals/ReceiveConfirmModal'
import { SendReviewModal } from '../components/inventory/modals/SendReviewModal'
import { AddProductModal } from '../components/inventory/modals/AddProductModal'

import {
  PendingTransfer,
} from '../services/inventory.service'
import {
  productService,
  NewProduct,
  StoreProductPayload,
  ProductListItem,
  StockAdjustmentPayload,
} from '../services/product.service'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/app/lib/http/client'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Mock pending transfers
const pendingSends: PendingTransfer[] = []
const pendingReceives: PendingTransfer[] = []

const ITEMS_PER_PAGE = 15

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function Inventory() {
  const queryClient = useQueryClient()

  // ── Filtros ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterCategoryId, setFilterCategoryId] = useState<string>('')
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // ── Selección ─────────────────────────────────────────────────────────────
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [selectedProductName, setSelectedProductName] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [toggleStatusConfirmOpen, setToggleStatusConfirmOpen] = useState(false)
  const [pendingToggleItem, setPendingToggleItem] = useState<ProductListItem | null>(null)

  // ── Modales ───────────────────────────────────────────────────────────────
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showReceiveConfirmModal, setShowReceiveConfirmModal] = useState(false)
  const [showSendReviewModal, setShowSendReviewModal] = useState(false)
  const [selectedTransfer, setSelectedTransfer] = useState<PendingTransfer | null>(null)

  // ── Query: Lista de productos ─────────────────────────────────────────────
  const queryParams = {
    page: currentPage,
    per_page: ITEMS_PER_PAGE,
    search: searchQuery || undefined,
    is_active:
      filterStatus === 'all'
        ? undefined
        : filterStatus === 'active'
          ? true
          : false,
    category_id: filterCategoryId ? Number(filterCategoryId) : undefined,
    department_id: filterDepartmentId ? Number(filterDepartmentId) : undefined,
    // Envía ?trashed=1 para obtener los eliminados (soft-deleted)
    trashed: showDeleted ? true : undefined,
  }

  const { data: productsResponse, isLoading, isFetching } = useQuery({
    queryKey: ['products', queryParams],
    queryFn: () => productService.getProducts(queryParams),
    placeholderData: (prev) => prev,
  })

  const products: ProductListItem[] = productsResponse?.data ?? []
  const paginationMeta = {
    total: productsResponse?.total ?? 0,
    from: productsResponse?.from ?? 0,
    to: productsResponse?.to ?? 0,
    last_page: productsResponse?.last_page ?? 1,
  }

  // ── Handlers de filtros ───────────────────────────────────────────────────
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }, [])

  const handleStatusChange = (value: string) => {
    setFilterStatus(value as 'all' | 'active' | 'inactive')
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearchQuery('')
    setFilterStatus('all')
    setFilterCategoryId('')
    setFilterDepartmentId('')
    setShowDeleted(false)
    setCurrentPage(1)
  }

  // ── Abrir modales ─────────────────────────────────────────────────────────
  const openDetail = (id: number) => {
    setSelectedProductId(id)
    setShowDetailModal(true)
  }

  const openEdit = (id: number) => {
    setSelectedProductId(id)
    setShowEditModal(true)
  }

  const openAdjust = (id: number) => {
    setSelectedProductId(id)
    setShowAdjustModal(true)
  }

  // ── Mutation: Toggle status ────────────────────────────────────────────────
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      productService.toggleStatus(id, isActive),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success(isActive ? 'Producto activado' : 'Producto desactivado')
    },
    onError: () => toast.error('Error al cambiar el estado del producto'),
  })

  const handleToggleStatusClick = (item: ProductListItem) => {
    setPendingToggleItem(item)
    setToggleStatusConfirmOpen(true)
  }

  const executeToggleStatus = () => {
    if (!pendingToggleItem) return
    toggleStatusMutation.mutate({ id: pendingToggleItem.id, isActive: !pendingToggleItem.is_active })
    setToggleStatusConfirmOpen(false)
    setPendingToggleItem(null)
  }

  // ── Mutation: Soft-delete ────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: number) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setDeleteConfirmOpen(false)
      setSelectedProductId(null)
      toast.success('Producto eliminado (puede restaurarse desde "Eliminados")')
    },
    onError: () => toast.error('Error al eliminar el producto'),
  })

  const handleDeleteClick = (item: ProductListItem) => {
    setSelectedProductId(item.id)
    setSelectedProductName(item.name)
    setDeleteConfirmOpen(true)
  }

  // ── Mutation: Restore ────────────────────────────────────────────────────
  const restoreMutation = useMutation({
    mutationFn: (id: number) => productService.restoreProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Producto restaurado correctamente')
    },
    onError: () => toast.error('Error al restaurar el producto'),
  })

  // ── Mutation: Agregar producto ────────────────────────────────────────────
  const addProductMutation = useMutation({
    mutationFn: async (payload: StoreProductPayload) => {
      const response = await productService.createProduct(payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setShowAddProductModal(false)
      toast.success('Producto agregado correctamente')
    },
    onError: () => toast.error('Error al agregar el producto'),
  })

  const handleAddProduct = async (product: NewProduct) => {


    const payload: StoreProductPayload = {
      name: product.name,
      barcode: product.barcode,
      description: product.description,
      category_id: Number(product.categoryId),
      department_id: Number(product.departmentId),
      product_type_id: Number(product.productTypeId),
      unit_id: Number(product.unitId),
      is_active: true,
      sat_product_key_id: product.satProductKeyId ? Number(product.satProductKeyId) : undefined,
      tax_ids: product.taxId ? [Number(product.taxId)] : [],
      price: {
        cost_without_taxes: Number(product.costWithoutTaxes) || 0,
        cost_with_taxes: Number(product.costWithTaxes) || 0,
        profit_percentage: Number(product.profitPercentage) || 0,
      },
      batch: product.requiresLot
        ? { batch_number: product.batchNumber, expiration_date: product.expirationDate }
        : undefined,
      heroImageFile: product.heroImage?.file,
      galleryFiles: product.galleryImages.map((img) => img.file),
      price_tiers: product.pricingTiers.map((t) => ({
        min_quantity: Number(t.minQuantity),
        profit_percentage: Number(t.profitPercentage),
        discount_percentage: Number(t.discountPercentage),
      })),
    }
    await addProductMutation.mutateAsync(payload)
  }

  // ── Mutation: Ajuste de stock ─────────────────────────────────────────────
  const adjustStockMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: StockAdjustmentPayload }) =>
      productService.adjustStock(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product', selectedProductId] })
      setShowAdjustModal(false)
      toast.success('Ajuste de stock aplicado')
    },
    onError: () => toast.error('Error al ajustar el stock'),
  })

  const handleApplyAdjustment = (data: {
    item: string
    type: 'add' | 'subtract'
    quantity: string
    reason: string
  }) => {
    if (!selectedProductId) return
    adjustStockMutation.mutate({
      id: selectedProductId,
      payload: { type: data.type, quantity: Number(data.quantity), reason: data.reason },
    })
  }

  // ── Transfer handlers ──────────────────────────────────────────────────────
  const handleTransfer = () => setShowTransferModal(false)
  const handleSendReview = (transfer: PendingTransfer) => {
    setSelectedTransfer(transfer)
    setShowTransferModal(false)
    setShowSendReviewModal(true)
  }
  const handleReceiveConfirm = (transfer: PendingTransfer) => {
    setSelectedTransfer(transfer)
    setShowTransferModal(false)
    setShowReceiveConfirmModal(true)
  }
  const handleConfirmReceive = () => {
    setShowReceiveConfirmModal(false)
    setSelectedTransfer(null)
  }
  const handleConfirmSend = () => {
    setShowSendReviewModal(false)
    setSelectedTransfer(null)
  }
  const handleExport = () => toast.info('Exportación en desarrollo')

  // ── Derived ────────────────────────────────────────────────────────────────
  const hasActiveFilters =
    searchQuery || filterStatus !== 'all' || filterCategoryId || filterDepartmentId

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6 p-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Productos
            {showDeleted && (
              <span className="ml-2 text-sm font-normal text-red-500">(eliminados)</span>
            )}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {paginationMeta.total > 0
              ? `${paginationMeta.total} productos${showDeleted ? ' eliminados' : ''}`
              : 'Gestiona el catálogo de productos'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Ver eliminados toggle */}
          <button
            onClick={() => { setShowDeleted(!showDeleted); setCurrentPage(1) }}
            title={showDeleted ? 'Ver activos' : 'Ver eliminados'}
            className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors ${showDeleted
              ? 'bg-red-100 text-red-700 hover:bg-red-200'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            <Trash2 className="h-4 w-4" />
            {showDeleted ? 'Ver activos' : 'Eliminados'}
          </button>
          <Button variant="outline" onClick={() => setShowTransferModal(true)}>
            <ArrowRightLeft className="mr-2 h-4 w-4" /> Transferencias
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="mr-2 h-4 w-4" /> Importar
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Exportar
          </Button>
          {!showDeleted && (
            <Button onClick={() => setShowAddProductModal(true)} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
              <Plus className="mr-2 h-4 w-4" /> Agregar Producto
            </Button>
          )}
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {/* Búsqueda */}
          <div className="sm:col-span-2 lg:col-span-1">
            <SearchFilter
              placeholder="Nombre o código de barras..."
              onSearch={handleSearch}
              initialValue={searchQuery}
            />
          </div>

          {/* Categoría */}
          <CatalogSelect
            catalog="categories"
            placeholder="Todas las categorías"
            value={filterCategoryId}
            onChange={(val: string) => {
              setFilterCategoryId(val === filterCategoryId ? '' : val)
              setCurrentPage(1)
            }}
          />

          {/* Departamento */}
          <CatalogSelect
            catalog="departments"
            placeholder="Todos los departamentos"
            value={filterDepartmentId}
            onChange={(val: string) => {
              setFilterDepartmentId(val === filterDepartmentId ? '' : val)
              setCurrentPage(1)
            }}
          />

          {/* Estado */}
          <div className="flex items-center gap-2">
            <Select value={filterStatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="shrink-0 rounded px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                title="Limpiar filtros"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabla ── */}
      {isLoading ? (
        <TableSkeleton
          headers={['Imagen', 'Producto', 'Barcode', 'Categoría', 'Tipo', 'Precio', 'Estado', 'Acciones']}
          columns={['avatar-text', 'text-two-lines', 'text', 'text', 'text', 'text', 'badge', 'actions']}
          rows={8}
        />
      ) : (
        <div className={`overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-opacity ${isFetching ? 'opacity-60' : 'opacity-100'}`}>
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Imagen</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Producto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    <div className="flex items-center gap-1"><Barcode className="h-3.5 w-3.5" /> Código de Barras</div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Categoría</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Precio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {products.length > 0 ? (
                  products.map((item) => (
                    <tr
                      key={item.id}
                      className={`transition-colors ${showDeleted ? 'bg-red-50/30 hover:bg-red-50' : 'hover:bg-slate-50'
                        }`}
                    >
                      {/* Imagen */}
                      <td className="px-6 py-4">
                        {item.hero_image ? (
                          <img
                            src={item.hero_image}
                            alt={item.name}
                            className="h-12 w-12 rounded-lg object-cover border border-slate-100 shadow-sm"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                            <ImageIcon className="h-5 w-5 text-slate-400" />
                          </div>
                        )}
                      </td>

                      {/* Nombre */}
                      <td className="px-6 py-4">
                        <p className={`font-medium ${showDeleted ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {item.name}
                        </p>
                        {item.description && (
                          <p className="mt-0.5 max-w-xs truncate text-xs text-slate-500">{item.description}</p>
                        )}
                      </td>

                      {/* Barcode */}
                      <td className="px-6 py-4">
                        {item.barcode ? (
                          <span className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
                            <Barcode className="h-3 w-3 shrink-0 text-slate-400" />
                            {item.barcode}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>

                      {/* Categoría */}
                      <td className="px-6 py-4 text-sm text-slate-700">{item.category?.name ?? '—'}</td>

                      {/* Tipo */}
                      <td className="px-6 py-4 text-sm text-slate-700">{item.product_type?.name ?? '—'}</td>

                      {/* Precio */}
                      <td className="px-6 py-4">
                        {item.price ? (
                          <div>
                            <p className="font-semibold text-slate-900">
                              ${Number(item.price.final_price ?? item.price.sale_price ?? 0).toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-500">
                              Costo: ${Number(item.price.cost_without_taxes).toFixed(2)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4">
                        {showDeleted ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                            Eliminado
                          </span>
                        ) : (
                          <Badge className={item.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                            {item.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {showDeleted ? (
                            /* Solo restaurar cuando se ven eliminados */
                            <button
                              onClick={() => restoreMutation.mutate(item.id)}
                              disabled={restoreMutation.isPending}
                              className="flex items-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50"
                              title="Restaurar producto"
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                            </button>
                          ) : (
                            <>
                              {/* Ver detalles */}
                              <button
                                onClick={() => openDetail(item.id)}
                                className="rounded p-1.5 hover:bg-slate-100"
                                title="Ver detalles"
                              >
                                <Eye className="h-4 w-4 text-slate-600" />
                              </button>
                              {/* Toggle activo/inactivo */}
                              <button
                                onClick={() => handleToggleStatusClick(item)}
                                disabled={toggleStatusMutation.isPending}
                                title={item.is_active ? 'Desactivar' : 'Activar'}
                                className={`rounded p-1.5 transition-colors ${item.is_active
                                  ? 'text-green-600 hover:bg-green-50'
                                  : 'text-slate-400 hover:bg-slate-100'
                                  }`}
                              >
                                {item.is_active ? (
                                  <ToggleRight className="h-5 w-5" />
                                ) : (
                                  <ToggleLeft className="h-5 w-5" />
                                )}
                              </button>
                              {/* Editar producto */}
                              <button
                                onClick={() => openEdit(item.id)}
                                className="rounded p-1.5 hover:bg-blue-50"
                                title="Editar producto"
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                              </button>
                              {/* Ajustar stock */}
                              <button
                                onClick={() => openAdjust(item.id)}
                                className="rounded p-1.5 hover:bg-orange-50"
                                title="Ajustar stock"
                              >
                                <Settings2 className="h-4 w-4 text-orange-500" />
                              </button>
                              {/* Eliminar (soft-delete) */}
                              <button
                                onClick={() => handleDeleteClick(item)}
                                className="rounded p-1.5 hover:bg-red-50"
                                title="Eliminar producto"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center">
                      <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                      <p className="text-slate-500">
                        {showDeleted
                          ? 'No hay productos eliminados'
                          : searchQuery
                            ? `Sin resultados para "${searchQuery}"`
                            : 'No hay productos registrados'}
                      </p>
                      {searchQuery && (
                        <p className="mt-1 text-xs text-slate-400">Intenta buscar por nombre o código de barras</p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {paginationMeta.total > 0 && (
            <GlobalPaginator
              totalItems={paginationMeta.total}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              from={paginationMeta.from}
              to={paginationMeta.to}
            />
          )}
        </div>
      )}

      {/* ── Modales ── */}

      {/* Detalle del producto */}
      <DetailModal
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        productId={selectedProductId}
      />

      {/* Edición completa */}
      <EditProductModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        productId={selectedProductId}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['products'] })}
      />

      {/* Ajuste de stock */}
      <AdjustModal
        open={showAdjustModal}
        onOpenChange={setShowAdjustModal}
        selectedItem={
          selectedProductId
            ? ({
              id: selectedProductId,
              name: products.find((p) => p.id === selectedProductId)?.name ?? '',
              stock: 0,
              minStock: 0,
              category: '',
              stockCentro: 0,
              stockNorte: 0,
              stockSur: 0,
              price: 0,
              cost: 0,
              branch: '',
              lot: '',
              expiry: '',
              lastMovement: '',
              supplier: '',
            } as any)
            : null
        }
        onApplyAdjustment={handleApplyAdjustment}
      />

      {/* Agregar producto */}
      <AddProductModal
        open={showAddProductModal}
        onOpenChange={setShowAddProductModal}
        onAddProduct={handleAddProduct}
      />

      {/* Transferencias */}
      <TransferModal
        open={showTransferModal}
        onOpenChange={setShowTransferModal}
        inventory={[]}
        pendingSends={pendingSends}
        pendingReceives={pendingReceives}
        onSendReview={handleSendReview}
        onReceiveConfirm={handleReceiveConfirm}
        onTransfer={handleTransfer}
      />

      <ImportModal open={showImportModal} onOpenChange={setShowImportModal} />

      <ReceiveConfirmModal
        open={showReceiveConfirmModal}
        onOpenChange={setShowReceiveConfirmModal}
        selectedTransfer={selectedTransfer}
        onConfirm={handleConfirmReceive}
      />

      <SendReviewModal
        open={showSendReviewModal}
        onOpenChange={setShowSendReviewModal}
        selectedTransfer={selectedTransfer}
        inventory={[]}
        onConfirm={handleConfirmSend}
      />

      {/* ── Modal: Confirmar Eliminar Producto ── */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setSelectedProductId(null) }}
        onConfirm={() => selectedProductId && deleteMutation.mutate(selectedProductId)}
        preset="delete"
        itemName={`producto "${selectedProductName}"`}
      />

      {/* ── Modal: Confirmar Cambio de Estado ── */}
      <ConfirmationModal
        isOpen={toggleStatusConfirmOpen}
        onClose={() => { setToggleStatusConfirmOpen(false); setPendingToggleItem(null) }}
        onConfirm={executeToggleStatus}
        preset="toggleStatus"
        itemName="producto"
      />
    </div>
  )
}
